import { useState } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { SectionHeading, ImageTag } from './SectionHeading';
import { RENDERS, PROPERTY, fadeUp } from './data';

const Lightbox = ({ index, setIndex }) => {
  const step = (d) => setIndex((prev) => (prev + d + RENDERS.length) % RENDERS.length);
  return (
    <Dialog open={index !== null} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent className="max-w-[95vw] max-h-[95vh] p-0 bg-black/95 border-none">
        <DialogTitle className="sr-only">Architectural renders</DialogTitle>
        <div className="relative w-full h-full flex items-center justify-center">
          <button onClick={() => setIndex(null)} className="absolute top-4 right-4 z-50 p-2 text-white/80 hover:text-white" data-testid="lightbox-close"><X size={24} /></button>
          <button onClick={() => step(-1)} className="absolute left-4 z-50 p-2 text-white/80 hover:text-white" data-testid="lightbox-prev"><ChevronLeft size={32} /></button>
          {index !== null && <img src={RENDERS[index]} alt="Architectural render" className="max-w-full max-h-[90vh] object-contain" />}
          <ImageTag>Architectural render</ImageTag>
          <button onClick={() => step(1)} className="absolute right-4 z-50 p-2 text-white/80 hover:text-white" data-testid="lightbox-next"><ChevronRight size={32} /></button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ArchitectureSection = () => {
  const [index, setIndex] = useState(null);
  return (
    <section id="architecture" className="py-20 md:py-28 bg-[#1a1e1a]" data-testid="home-section">
      <SectionHeading eyebrow="Architecture" className="mb-10">Three volumes, shaped to the land</SectionHeading>
      <motion.div {...fadeUp} className="max-w-3xl mx-auto px-6 md:px-12 mb-14 space-y-5 text-white/85 text-base md:text-lg leading-relaxed" data-testid="concept-section">
        <p>
          Three volumes follow the contours of the site and step down the landscape. The main living
          pavilion sits beneath a single monopitch roof that wraps down the entry elevation; skylights
          punctuate the hallway and extensive glazing frames the valley views.
        </p>
        <p>
          The garage sits between the volumes as a quieter element, and a connecting canopy and deck
          tie the composition together. Profiled metal roofing, timber cladding, concrete and thermally
          broken glazing make up the material palette.
        </p>
        <p className="text-[#d6cdb4] text-sm tracking-[0.12em] uppercase" data-testid="architect-credit">
          Architecture by {PROPERTY.architect} · Built by NF Construction
        </p>
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-3">
        {RENDERS.map((render, i) => (
          <motion.button
            key={render}
            type="button"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden aspect-[4/3]"
            onClick={() => setIndex(i)}
            data-testid={`home-render-${i}`}
          >
            <img src={render} alt={`Architectural render ${i + 1}`} className="w-full h-full object-cover" />
            <ImageTag>Architectural render</ImageTag>
          </motion.button>
        ))}
      </div>
      <Lightbox index={index} setIndex={setIndex} />
    </section>
  );
};
